import { createSelector } from "reselect";
import { formatDate } from "utils/formatDate";
import { formatRegion } from "utils/formatRegion";
import {
  DATE_FORMATS,
  ORG_STATUSES_LABELS,
  PROJECT_STATUS_LABELS
} from "../../constants";
import { RootState } from "../../reducers";
import {
  BILLING_RESOURCES_TYPES,
  BILLING_RESOURCES_TYPES_TITLES,
  BILLING_ROLES_LABELS,
  OrganizationBillingData,
  ProjectBillingBackupsLicensesData,
  ProjectBillingBackupsStorageData,
  ProjectBillingData,
  ProjectBillingDatabasesData,
  ProjectBillingInstancesData,
  ProjectBillingS3Data,
  ProjectBillingVolumesData,
  TableBillingBackupsLicensesRecord,
  TableBillingBackupsStorageRecord,
  TableBillingDatabasesRecord,
  TableBillingInstancesRecord,
  TableBillingVolumesRecord,
  TableOrganizationProjectBillingData,
  TableOrganizationsBillingData,
  TableTotalBillingBackupsLicensesRecord,
  TableTotalBillingBackupsStorageRecord,
  TableTotalBillingDatabasesRecord,
  TableTotalBillingInstancesRecord,
  TableTotalBillingVolumesRecord,
  TableTotalOrganizationsBillingData,
  UserBillingData
} from "./types";
// import { org_bil } from "./drafts";

// const formatCosts = (costsData?: Record<string, number>) => {
//   return costsData
//     ? Object.keys(costsData).reduce(
//         (acc, cur) => ({
//           ...acc,
//           [`cost-${cur}`]: costsData[cur].toFixed(2)
//         }),
//         {}
//       )
//     : {};
// };

const formatCosts = (
  costsData?: Record<string, number>
): Record<string, string> => {
  if (!costsData) return {};

  return Object.keys(costsData).reduce<Record<string, string>>((acc, cur) => {
    acc[`cost-${cur}`] = costsData[cur].toFixed(2);
    return acc;
  }, {});
};

export const userBillingDataSelector = (
  state: RootState
): UserBillingData | null => state.billing.userBillingData;
// ): UserBillingData | null => org_bil;

export const isUserBillingDataLoadingSelector = (state: RootState): boolean =>
  state.billing.isUserBillingDataLoading;

export const tableOrganizationsBillingDataSelector = createSelector(
  [userBillingDataSelector],
  (userBillingData): TableOrganizationsBillingData[] | null =>
    userBillingData?.organizations?.map((organization) => ({
      id: organization.id,
      name: organization.name,
      status: ORG_STATUSES_LABELS[organization.status],
      billing_role: BILLING_ROLES_LABELS[organization.billing_role],
      billing_admins_str: organization.billing_admins.join(" \n"),
      ...formatCosts(organization.costs)
    })) ?? null
);

export const tableOrganizationsTotalBillingDataSelector = createSelector(
  [userBillingDataSelector],
  (userBillingData): TableTotalOrganizationsBillingData | null =>
    userBillingData && (userBillingData.organizations?.length ?? 0) > 0
      ? {
          id: "__total",
          name: "Total",
          status: "",
          billing_role: "",
          billing_admins_str: "",
          ...formatCosts(userBillingData.costs)
        }
      : null
);

export const isOrganizationBillingDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isOrganizationBillingDataLoading;

export const organizationBillingDataSelector = (
  state: RootState
): OrganizationBillingData | null => state.billing.organizationBillingData;

export const tableOrganizationProjectsBillingDataSelector = createSelector(
  [organizationBillingDataSelector],
  (organizationBillingData): TableOrganizationProjectBillingData[] | null =>
    organizationBillingData?.projects?.map((project) => ({
      id: project.id,
      name: project.name,
      status: PROJECT_STATUS_LABELS[project.status],
      region: formatRegion(project.region),
      ...formatCosts(project.costs)
    })) ?? null
);

export const tableOrganizationTotalBillingDataSelector = createSelector(
  [organizationBillingDataSelector],
  (organizationBillingData): TableOrganizationProjectBillingData | null =>
    organizationBillingData &&
    (organizationBillingData.projects?.length ?? 0) > 0
      ? {
          id: "__total",
          name: "Total",
          status: "",
          region: "",
          ...formatCosts(organizationBillingData.costs)
        }
      : null
);

export const projectBillingDataSelector = (
  state: RootState
): ProjectBillingData | null => state.billing.projectBillingData;

export const isProjectBillingDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingDataLoading;

export const isProjectBudgetLimitUpdatingSelector = (
  state: RootState
): boolean => state.billing.isProjectBudgetLimitUpdating;

export const isProjectBillingInstancesDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingInstancesDataLoading;

export const isProjectBillingVolumesDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingVolumesDataLoading;

export const isProjectBillingDatabasesDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingDatabasesDataLoading;

export const isProjectBillingS3DataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingS3DataLoading;

export const isProjectBillingBackupsStorageDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingBackupsStorageDataLoading;

export const isProjectBillingBackupsLicensesDataLoadingSelector = (
  state: RootState
): boolean => state.billing.isProjectBillingBackupsLicensesDataLoading;

export const projectBillingInstancesDataSelector = (
  state: RootState
): ProjectBillingInstancesData | null =>
  state.billing.projectBillingInstancesData;

export const tableProjectBillingInstancesRecordsSelector = createSelector(
  [projectBillingInstancesDataSelector],
  (projectBillingInstancesData): TableBillingInstancesRecord[] | null => {
    if (!Array.isArray(projectBillingInstancesData?.items)) return null;
    return (
      projectBillingInstancesData?.items.map((record) => ({
        id: record.id,
        type: record.type,
        name: record.name,
        status: record.deleted ? "deleted" : "available",
        deleted: record.deleted
          ? formatDate(new Date(record.deleted), DATE_FORMATS.DATETIME)
          : "",
        itemFlavors: record.itemFlavors.map((flavor) => ({
          id: flavor.name,
          name: flavor.name,
          description: flavor.description,
          stopped_time_hours: flavor.stopped_time_cost?.hours ?? null,
          stopped_time_cost: flavor.stopped_time_cost?.cost ?? null,
          ...formatCosts(flavor.costs)
        })),
        ...formatCosts(record.costs)
      })) || null
    );
  }
);

// export const tableProjectBillingInstancesRecordsSelector = (
//   state: RootState
// ): TableBillingInstancesRecord[] | null =>
//   state.billing.projectBillingInstancesData?.items.map((record) => ({
//     id: record.id,
//     type: record.type,
//     name: record.name,
//     status: record.deleted ? "deleted" : "available",
//     deleted: record.deleted
//       ? formatDate(new Date(record.deleted), DATE_FORMATS.DATETIME)
//       : "",
//     itemFlavors: record.itemFlavors.map((flavor) => ({
//       name: flavor.name,
//       description: flavor.description,
//       ...formatCosts(flavor.costs)
//     })),
//     ...formatCosts(record.costs)
//   })) || null;

export const tableProjectBillingInstancesRecordsTotalSelector = (
  state: RootState
): TableTotalBillingInstancesRecord | null =>
  state.billing.projectBillingInstancesData &&
  Array.isArray(state.billing.projectBillingInstancesData?.items) &&
  state.billing.projectBillingInstancesData.items.length > 0
    ? {
        id: "Total",
        name: "",
        deleted: "",
        itemFlavors: "",
        status: "",
        type: "",
        ...formatCosts(state.billing.projectBillingInstancesData.costs)
      }
    : null;

// volumes tab
export const projectBillingVolumesDataSelector = (
  state: RootState
): ProjectBillingVolumesData | null => state.billing.projectBillingVolumesData;

export const tableProjectBillingVolumesRecordsSelector = createSelector(
  [projectBillingVolumesDataSelector],
  (projectBillingVolumesData): TableBillingVolumesRecord[] | null => {
    if (!Array.isArray(projectBillingVolumesData?.items)) return null;
    return (
      projectBillingVolumesData?.items.map((record) => ({
        id: record.id,
        name: record.name ? record.name : "",
        type: record.type,
        deleted: record.deleted, // "true" or "false"
        status: record.deleted === "true" ? "deleted" : "available",
        itemFlavors: record.itemFlavors.map((flavor) => ({
          id: flavor.name,
          name: flavor.name,
          description: flavor.description,
          ...formatCosts(flavor.costs)
        })),
        ...formatCosts(record.costs)
      })) || null
    );
  }
);

// export const tableProjectBillingVolumesRecordsSelector = (
//   state: RootState
// ): TableBillingVolumesRecord[] | null =>
//   state.billing.projectBillingVolumesData?.items.map((record) => ({
//     id: record.id,
//     name: record.name ? record.name : "",
//     type: record.type,
//     deleted: record.deleted, // "true" or "false"
//     status: record.deleted === "true" ? "deleted" : "available",
//     itemFlavors: record.itemFlavors.map((flavor) => ({
//       name: flavor.name,
//       description: flavor.description,
//       ...formatCosts(flavor.costs)
//     })),
//     ...formatCosts(record.costs)
//   })) || null;

export const tableProjectBillingVolumesRecordsTotalSelector = (
  state: RootState
): TableTotalBillingVolumesRecord | null =>
  state.billing.projectBillingVolumesData &&
  Array.isArray(state.billing.projectBillingVolumesData?.items) &&
  state.billing.projectBillingVolumesData.items.length > 0
    ? {
        id: "Total",
        deleted: "",
        status: "",
        name: "",
        itemFlavors: "",
        type: "",
        ...formatCosts(state.billing.projectBillingVolumesData.costs)
      }
    : null;

// databases tab
export const projectBillingDatabasesDataSelector = (
  state: RootState
): ProjectBillingDatabasesData | null =>
  state.billing.projectBillingDatabasesData;

export const tableProjectBillingDatabasesRecordsSelector = createSelector(
  [projectBillingDatabasesDataSelector],
  (projectBillingDatabasesData): TableBillingDatabasesRecord[] | null => {
    if (!Array.isArray(projectBillingDatabasesData?.items)) return null;
    return (
      projectBillingDatabasesData?.items.map((record) => ({
        id: record.id,
        name: record.name,
        type: record.type,
        status: record.deleted === "true" ? "deleted" : "available",
        deleted: record.deleted,
        itemFlavors: record.itemFlavors.map((flavor) => ({
          id: flavor.name,
          name: flavor.name,
          ...formatCosts(flavor.costs)
        })),
        ...formatCosts(record.costs)
      })) || null
    );
  }
);

// export const tableProjectBillingDatabasesRecordsSelector = (
//   state: RootState
// ): TableBillingDatabasesRecord[] | null =>
//   state.billing.projectBillingDatabasesData?.items.map((record) => ({
//     id: record.id,
//     name: record.name,
//     type: record.type,
//     status: record.deleted === "true" ? "deleted" : "available",
//     deleted: record.deleted,
//     itemFlavors: record.itemFlavors.map((flavor) => ({
//       name: flavor.name,
//       ...formatCosts(flavor.costs)
//     })),
//     ...formatCosts(record.costs)
//   })) || null;

export const tableProjectBillingDatabasesRecordsTotalSelector = createSelector(
  [projectBillingDatabasesDataSelector],
  (projectBillingDatabasesData): TableTotalBillingDatabasesRecord | null => {
    return projectBillingDatabasesData &&
      Array.isArray(projectBillingDatabasesData?.items) &&
      projectBillingDatabasesData.items.length > 0
      ? {
          id: "Total",
          name: "",
          deleted: "",
          status: "",
          itemFlavors: "",
          type: "",
          ...formatCosts(projectBillingDatabasesData.costs)
        }
      : null;
  }
);

// s3 tab
export const projectBillingS3DataSelector = (
  state: RootState
): ProjectBillingS3Data | null => state.billing.projectBillingS3Data;

export const tableProjectBillingS3RecordsSelector = createSelector(
  [projectBillingS3DataSelector],
  (projectBillingS3Data) => {
    if (!Array.isArray(projectBillingS3Data?.items)) return null;
    return (
      projectBillingS3Data?.items.map((record) => ({
        id: record.id,
        name: record.name,
        type: record.type,
        status: record.deleted === "true" ? "deleted" : "available",
        deleted: record.deleted,
        itemFlavors:
          record.itemFlavors?.map((flavor) => ({
            id: flavor.name,
            name: flavor.name,
            ...formatCosts(flavor.costs)
          })) || null,
        ...formatCosts(record.costs)
      })) || null
    );
  }
);

export const tableProjectBillingS3RecordsTotalSelector = createSelector(
  [projectBillingS3DataSelector],
  (projectBillingS3Data) => {
    return projectBillingS3Data &&
      Array.isArray(projectBillingS3Data?.items) &&
      projectBillingS3Data.items.length > 0
      ? {
          id: "Total",
          name: "",
          deleted: "",
          status: "",
          itemFlavors: "",
          type: "",
          ...formatCosts(projectBillingS3Data.costs)
        }
      : null;
  }
);

// backups tab storage
export const projectBillingBackupsStorageDataSelector = (
  state: RootState
): ProjectBillingBackupsStorageData | null =>
  state.billing.projectBillingBackupsStorageData;
// ): ProjectBillingBackupsStorageData | null => billingBackupsStorageDraft;

export const tableProjectBillingBackupsStorageRecordsSelector = (
  state: RootState
): TableBillingBackupsStorageRecord[] | null => {
  if (!Array.isArray(state.billing.projectBillingBackupsStorageData?.items))
    return null;
  return (
    state.billing.projectBillingBackupsStorageData?.items.map((record) => ({
      // billingBackupsStorageDraft?.items.map((record) => ({
      id: record.id,
      name: record.name ? record.name : "",
      type: record.type,
      // deleted: record.deleted, // "true" or "false"
      // status: record.deleted === "true" ? "deleted" : "available",
      itemFlavors: record.itemFlavors.map((flavor) => ({
        name: flavor.name,
        id: flavor.id,
        size: `${(Number(flavor.size) / 1024 ** 3).toFixed(2)} GiB`,
        description: flavor.description,
        created_at: formatDate(
          new Date(flavor.created_at),
          DATE_FORMATS.DATETIME
        ),
        status: flavor.status ? "deleted" : "available",
        deleted: flavor.status
          ? formatDate(new Date(flavor.status), DATE_FORMATS.DATETIME)
          : null,
        ...formatCosts(flavor.costs)
      })),
      ...formatCosts(record.costs)
    })) || null
  );
};

export const tableProjectBillingBackupsStorageRecordsTotalSelector = (
  state: RootState
): TableTotalBillingBackupsStorageRecord | null =>
  // billingBackupsStorageDraft && billingBackupsStorageDraft.items.length > 0
  state.billing.projectBillingBackupsStorageData &&
  Array.isArray(state.billing.projectBillingBackupsStorageData?.items) &&
  state.billing.projectBillingBackupsStorageData.items.length > 0
    ? {
        id: "Total storage cost",
        deleted: "",
        status: "",
        name: "",
        itemFlavors: "",
        type: "",
        // ...formatCosts(billingBackupsStorageDraft.costs)
        ...formatCosts(state.billing.projectBillingBackupsStorageData.costs)
      }
    : null;

// backups tab licenses
export const projectBillingBackupsLicensesDataSelector = (
  state: RootState
): ProjectBillingBackupsLicensesData | null =>
  state.billing.projectBillingBackupsLicensesData;
// ): ProjectBillingBackupsLicensesData | null => billingBackupsLicensesDraft;

export const tableProjectBillingBackupsLicensesRecordsSelector = (
  state: RootState
): TableBillingBackupsLicensesRecord[] | null => {
  if (!Array.isArray(state.billing.projectBillingBackupsLicensesData?.items))
    return null;
  return (
    state.billing.projectBillingBackupsLicensesData?.items.map((record) => ({
      // billingBackupsLicensesDraft?.items.map((record) => ({
      id: record.id,
      name: record.name ? record.name : "",
      type: record.type,
      // deleted: record.deleted, // "true" or "false"
      // status: record.deleted === "true" ? "deleted" : "available",
      itemFlavors: record.itemFlavors.map((flavor) => ({
        name: flavor.name,
        id: flavor.id,
        size: `${(Number(flavor.size) / 1024 ** 3).toFixed(2)} GiB`,
        description: flavor.description,
        created_at: formatDate(
          new Date(flavor.created_at),
          DATE_FORMATS.DATETIME
        ),
        status: flavor.status ? "deleted" : "available",
        deleted: flavor.status
          ? formatDate(new Date(flavor.status), DATE_FORMATS.DATETIME)
          : null
      })),
      ...formatCosts(record.costs)
    })) || null
  );
};

export const tableProjectBillingBackupsLicensesRecordsTotalSelector = (
  state: RootState
): TableTotalBillingBackupsLicensesRecord | null =>
  // billingBackupsLicensesDraft && billingBackupsLicensesDraft.items.length > 0
  state.billing.projectBillingBackupsLicensesData &&
  Array.isArray(state.billing.projectBillingBackupsLicensesData?.items) &&
  state.billing.projectBillingBackupsLicensesData.items.length > 0
    ? {
        id: "Total licenses cost",
        deleted: "",
        status: "",
        name: "",
        itemFlavors: "",
        type: "",
        // ...formatCosts(billingBackupsLicensesDraft.costs)
        ...formatCosts(state.billing.projectBillingBackupsLicensesData.costs)
      }
    : null;

// summary tab

const getInstancesCosts = (state: RootState) =>
  state.billing.projectBillingInstancesData?.costs;
const getVolumesCosts = (state: RootState) =>
  state.billing.projectBillingVolumesData?.costs;
const getDatabasesCosts = (state: RootState) =>
  state.billing.projectBillingDatabasesData?.costs;
const getS3Costs = (state: RootState) =>
  state.billing.projectBillingS3Data?.costs;
const getBackupsStorageCosts = (state: RootState) =>
  state.billing.projectBillingBackupsStorageData?.costs;
// billingBackupsStorageDraft.costs;
const getBackupsLicensesCosts = (state: RootState) =>
  state.billing.projectBillingBackupsLicensesData?.costs;
// billingBackupsLicensesDraft.costs;

export const tableProjectBillingSummaryRecordsSelector = createSelector(
  [
    getInstancesCosts,
    getVolumesCosts,
    getDatabasesCosts,
    getS3Costs,
    getBackupsStorageCosts,
    getBackupsLicensesCosts
  ],
  (
    instancesCosts,
    volumesCosts,
    databasesCosts,
    s3Costs,
    backupsStorageCosts,
    backupsLicensesCosts
  ) => {
    const mergeCosts = (
      a?: Record<string, number>,
      b?: Record<string, number>
    ) => {
      const result: Record<string, number> = { ...a };

      if (b) {
        for (const [currency, value] of Object.entries(b)) {
          result[currency] = (result[currency] || 0) + value;
        }
      }

      return result;
    };

    const totalBackupsCosts = mergeCosts(
      backupsStorageCosts,
      backupsLicensesCosts
    );

    const hasAnyCosts =
      Boolean(instancesCosts) ||
      Boolean(volumesCosts) ||
      Boolean(databasesCosts) ||
      Boolean(s3Costs) ||
      Boolean(backupsStorageCosts) ||
      Boolean(backupsLicensesCosts);

    if (!hasAnyCosts) return [];

    return [
      {
        id: BILLING_RESOURCES_TYPES_TITLES[BILLING_RESOURCES_TYPES.INSTANCES],
        ...formatCosts(instancesCosts)
      },
      {
        id: BILLING_RESOURCES_TYPES_TITLES[BILLING_RESOURCES_TYPES.VOLUMES],
        ...formatCosts(volumesCosts)
      },
      {
        id: BILLING_RESOURCES_TYPES_TITLES[BILLING_RESOURCES_TYPES.DATABASES],
        ...formatCosts(databasesCosts)
        // Uncomment and update this part if you need to add collapsed data to the summary tab
        // items: state.billing.projectBillingDatabasesData?.items.map((item) => ({
        //   id: item.id,
        //   ...formatCosts(item.costs)
        // })),
      },
      {
        id: BILLING_RESOURCES_TYPES_TITLES[BILLING_RESOURCES_TYPES.S3],
        ...formatCosts(s3Costs)
      },
      {
        id: BILLING_RESOURCES_TYPES_TITLES[BILLING_RESOURCES_TYPES.BACKUPS],
        ...formatCosts(totalBackupsCosts)
      }
    ];
  }
);

export const tableProjectBillingSummaryRecordsTotalSelector = createSelector(
  [
    getInstancesCosts,
    getVolumesCosts,
    getDatabasesCosts,
    getS3Costs,
    getBackupsStorageCosts,
    getBackupsLicensesCosts
  ],
  (
    instancesCosts,
    volumesCosts,
    databasesCosts,
    s3Costs,
    backupsStorageCosts,
    backupsLicensesCosts
  ) => {
    const totalCosts = { chf: 0, eur: 0 };

    [
      instancesCosts,
      volumesCosts,
      databasesCosts,
      s3Costs,
      backupsStorageCosts,
      backupsLicensesCosts
    ].forEach((costs) => {
      if (costs) {
        Object.keys(totalCosts).forEach((currency) => {
          totalCosts[currency] += costs[currency] || 0;
        });
      }
    });

    return {
      id: "Total",
      ...formatCosts(totalCosts)
    };
  }
);
